import {
  AlertTriangle,
  Bell,
  Clock,
  CloudUpload,
  CreditCard,
  Download,
  Link2,
  Palette,
  Receipt,
  Search,
  Sparkles,
  User,
} from "lucide-react";

import { PLANS_ON_SALE, hasCommercialPlan } from "../../utils/agencyFeatures.js";
import { BILLING_DETAILS_SECTION, settingsPath } from "./settingsPaths.js";

const GROUPS = [
  { id: "account", title: "De la cuenta" },
  { id: "agency", title: "De la agencia" },
  { id: "platform", title: "De la plataforma" },
];

const isAgencyAdmin = (user) => user?.role === "agency_admin";
const isSuperadmin = (user) => user?.role === "superadmin";

// `tab`: pestaña de UserSettings (antiguo /user?tab=); `kind`: qué página pinta la sección.
const SECTIONS = [
  { id: "perfil", tab: "personal", kind: "user", group: "account", label: "Perfil", icon: User },
  { id: "notificaciones", tab: "notifications", kind: "user", group: "account", label: "Notificaciones", icon: Bell },
  {
    id: "plan",
    kind: "plan",
    group: "agency",
    label: "Plan y facturación",
    icon: CreditCard,
    visible: (user) => PLANS_ON_SALE && isAgencyAdmin(user) && hasCommercialPlan(user),
  },
  {
    id: BILLING_DETAILS_SECTION,
    kind: "billing-details",
    group: "agency",
    label: "Datos de facturación",
    icon: Receipt,
    visible: isAgencyAdmin,
  },
  {
    id: "bono-horas",
    tab: "hour-package",
    kind: "user",
    group: "agency",
    label: "Horas de tickets",
    icon: Clock,
    visible: isAgencyAdmin,
  },
  {
    id: "apariencia",
    tab: "appearance",
    kind: "user",
    // El admin configura aquí la marca blanca de la agencia
    group: (user) => (isAgencyAdmin(user) ? "agency" : "account"),
    label: (user) => (isAgencyAdmin(user) ? "Apariencia y marca" : "Apariencia"),
    icon: Palette,
    visible: (user) => user?.role !== "freelance_ticket",
  },
  {
    id: "prompts",
    tab: "prompts",
    kind: "user",
    // El admin edita los prompts de su agencia (copiloto y adaptación); el superadmin, los de la plataforma
    group: (user) => (isAgencyAdmin(user) ? "agency" : "platform"),
    label: "Prompts de IA",
    icon: Sparkles,
    visible: (user) => isAgencyAdmin(user) || isSuperadmin(user),
  },
  { id: "freelance-tickets", tab: "ticket-freelancers", kind: "user", group: "platform", label: "Freelance tickets", icon: AlertTriangle, visible: isSuperadmin },
  { id: "clientify", tab: "clientify", kind: "user", group: "platform", label: "Clientify", icon: Link2, visible: isSuperadmin },
  { id: "se-ranking", tab: "seranking", kind: "user", group: "platform", label: "SE Ranking", icon: Search, visible: isSuperadmin },
  { id: "podio", tab: "podio", kind: "user", group: "platform", label: "Migración de Podio", icon: CloudUpload, visible: isSuperadmin },
  { id: "descargas-csv", tab: "csv-exports", kind: "user", group: "platform", label: "Descargas CSV", icon: Download, visible: isSuperadmin },
];

export const getSettingsSections = (user) =>
  SECTIONS.filter((section) => !section.visible || section.visible(user)).map((section) => ({
    ...section,
    group: typeof section.group === "function" ? section.group(user) : section.group,
    label: typeof section.label === "function" ? section.label(user) : section.label,
    path: settingsPath(section.id),
    badge: section.id === "plan" && user.agencyPlan.past_due ? "!" : undefined,
  }));

export const getGroupedSettingsSections = (user) => {
  const sections = getSettingsSections(user);
  return GROUPS.map((group) => ({ ...group, items: sections.filter((section) => section.group === group.id) })).filter(
    (group) => group.items.length > 0
  );
};

// Antiguo /user?tab=<tab> → sección de Ajustes
export const settingsSectionForTab = (tab) => SECTIONS.find((section) => section.tab === tab)?.id || "perfil";
